<script lang="ts">
  // 6 · First client: name, email, rate, colour and a few activities.
  // After saving, the step shows what was created instead of the form.
  import { Check, Plus } from '@lucide/svelte';
  import { t } from '../../i18n.svelte';
  import { data } from '../../stores/data.svelte';
  import { Button, Chip, ChipGroup, ColorSwatches, Field, Input, MoneyInput, Notice } from '../../ui';
  import StepIntro from './StepIntro.svelte';
  import { flow, PRESET_ACTIVITIES } from '../state.svelte';

  const presetNames = $derived(PRESET_ACTIVITIES.map((k) => t(`onb.act.${k}`)));
  const customChosen = $derived(flow.chosen.filter((name) => !presetNames.includes(name)));

  function onCustomKey(event: KeyboardEvent) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    flow.addCustom();
  }
</script>

{#if flow.created}
  <StepIntro kicker={t('onb.client.kicker')} title={t('onb.client.created', { name: flow.created.name })} lead={t('onb.client.created_text', { count: flow.created.count })} success />
{:else}
  <StepIntro kicker={t('onb.client.kicker')} title={t('onb.client.title')} lead={t('onb.client.text')} focus={false} />
  {#if data.clients.length > 0}
    <Notice variant="dashed" tone="neutral" icon={false}>{t('onb.client.existing', { count: data.clients.length })}</Notice>
  {/if}
  <div class="stack">
    <Field label={t('onb.client.name')} for="onb-client-name" error={flow.clientError}>
      <Input
        id="onb-client-name"
        bind:value={flow.clientName}
        placeholder={t('onb.client.name_placeholder')}
        maxlength={100}
        autocomplete="organization"
        invalid={!!flow.clientError}
        data-step-focus
        oninput={() => (flow.clientError = null)}
      />
    </Field>
    <div class="grid-2">
      <Field label={t('onb.client.email')} optional optionalLabel={t('common.optional')} for="onb-client-email" error={flow.emailError}>
        <Input
          id="onb-client-email"
          type="email"
          bind:value={flow.clientEmail}
          placeholder={t('onb.client.email_placeholder')}
          autocomplete="email"
          invalid={!!flow.emailError}
          oninput={() => (flow.emailError = null)}
        />
      </Field>
      <Field label={t('onb.client.rate')} for="onb-client-rate">
        <MoneyInput id="onb-client-rate" bind:value={flow.clientRate} suffix={t('common.per_hour')} />
      </Field>
    </div>
    <Field label={t('onb.client.color')}>
      <ColorSwatches bind:value={flow.clientColor} ariaLabel={t('onb.client.color')} />
    </Field>
    <Field label={t('onb.client.activities')} hint={t('onb.client.activities_hint')}>
      <ChipGroup ariaLabel={t('onb.client.activities')}>
        {#each presetNames as name (name)}
          {@const on = flow.chosen.includes(name)}
          <Chip label={name} icon={on ? Check : Plus} selected={on} onclick={() => flow.toggleActivity(name)} />
        {/each}
        {#each customChosen as name (name)}
          <Chip label={name} icon={Check} selected onremove={() => flow.toggleActivity(name)} />
        {/each}
      </ChipGroup>
      <div class="custom">
        <label class="sr-only" for="onb-custom-act">{t('onb.client.custom')}</label>
        <Input id="onb-custom-act" bind:value={flow.customAct} placeholder={t('onb.client.custom_placeholder')} maxlength={100} onkeydown={onCustomKey} />
        <Button size="sm" disabled={!flow.customAct.trim()} onclick={() => flow.addCustom()}>{t('onb.client.add')}</Button>
      </div>
    </Field>
  </div>
{/if}

<style>
  .custom { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: var(--space-2); max-width: 380px; }
</style>
